import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';

// Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import AdminLayout from './components/AdminLayout';
import MaintenanceGuard from './components/MaintenanceGuard';
import NotificationCenter from './components/NotificationCenter';
import DevTrackerAssistant from './components/DevTrackerAssistant';
import SystemUpdateBanner from './components/SystemUpdateBanner';
import ErrorBoundary from './components/ErrorBoundary';
import { useAuth } from './context/AuthContext';
// Pages (Public/User)
import Home from './pages/Home';
const About = React.lazy(() => import('./pages/About'));
const Documentation = React.lazy(() => import('./pages/Documentation'));
const Changelog = React.lazy(() => import('./pages/Changelog'));
const Contact = React.lazy(() => import('./pages/Contact'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const Terms = React.lazy(() => import('./pages/Terms'));
const Login = React.lazy(() => import('./pages/Login'));
const Register = React.lazy(() => import('./pages/Register'));
const VerifyEmail = React.lazy(() => import('./pages/VerifyEmail'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const TwoFactorSetup = React.lazy(() => import('./pages/TwoFactorSetup'));
const TwoFactorVerify = React.lazy(() => import('./pages/TwoFactorVerify'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const PublicProfile = React.lazy(() => import('./pages/PublicProfile'));
const EmbedWidget = React.lazy(() => import('./pages/EmbedWidget'));
const AdminRegister = React.lazy(() => import('./pages/AdminRegister'));
const AdminLogin = React.lazy(() => import('./pages/AdminLogin'));
const Resume = React.lazy(() => import('./pages/Resume'));
const WhatIsNew = React.lazy(() => import('./pages/WhatIsNew'));
const FAQ = React.lazy(() => import('./pages/FAQ'));

// Pages (Admin)
const AdminDashboard = React.lazy(() => import('./pages/admin/AdminDashboard'));
const AdminUsers = React.lazy(() => import('./pages/admin/AdminUsers'));
const AdminProfiles = React.lazy(() => import('./pages/admin/AdminProfiles'));
const AdminMessages = React.lazy(() => import('./pages/admin/AdminMessages'));
const AdminLogs = React.lazy(() => import('./pages/admin/AdminLogs'));
const AdminSettings = React.lazy(() => import('./pages/admin/AdminSettings'));
const AdminStatistics = React.lazy(() => import('./pages/admin/AdminStatistics'));
const AdminAdmins = React.lazy(() => import('./pages/admin/AdminAdmins'));
const AdminDocumentation = React.lazy(() => import('./pages/admin/AdminDocumentation'));
const AdminReports = React.lazy(() => import('./pages/admin/AdminReports'));
const AdminChangelog = React.lazy(() => import('./pages/admin/AdminChangelog'));
const AdminNotifications = React.lazy(() => import('./pages/admin/AdminNotifications'));
const AdminAIHub = React.lazy(() => import('./pages/admin/AdminAIHub'));
const AdminSecurityReview = React.lazy(() => import('./pages/admin/AdminSecurityReview'));
const AdminConsultations = React.lazy(() => import('./pages/admin/AdminConsultations'));
const AdminErrors = React.lazy(() => import('./pages/admin/AdminErrors'));
const AdminSecurityDossier = React.lazy(() => import('./pages/admin/AdminSecurityDossier'));
const AdminSitemap = React.lazy(() => import('./pages/admin/AdminSitemap'));
const AdminIdentitySearch = React.lazy(() => import('./pages/admin/AdminIdentitySearch'));
const AdminDNAProtocol = React.lazy(() => import('./pages/admin/AdminDNAProtocol'));

// New Systems
const VerificationPortal = React.lazy(() => import('./pages/VerificationPortal'));
const DigitalID = React.lazy(() => import('./pages/DigitalID'));
const VerificationGuide = React.lazy(() => import('./pages/VerificationGuide'));
const ColabHub = React.lazy(() => import('./pages/ColabHub'));
const BharatVerifyColab = React.lazy(() => import('./pages/BharatVerifyColab'));
const DAIVXColab = React.lazy(() => import('./pages/DAIVXColab'));
const NotFound = React.lazy(() => import('./pages/NotFound'));

// Components

// User Restricted
const Suspended = React.lazy(() => import('./pages/Suspended'));
const Report = React.lazy(() => import('./pages/Report'));

function AppContent() {
  const { userProfile } = useAuth();

  return (
    <Router>
      <SystemUpdateBanner />
      <MaintenanceGuard>
        <ErrorBoundary userId={userProfile?.suid || userProfile?.adminId}>
          <div className="app-layout">
            <React.Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', color: '#00e5ff', fontFamily: 'monospace', fontSize: '1.2rem', backgroundColor: '#0a0a0a' }}>INITIALIZING DAIV-1000...</div>}>
              <Routes>
              {/* --- ADMIN PANEL --- */}
              <Route path="/admin" element={
                <AdminRoute>
                  <AdminLayout />
                </AdminRoute>
              }>
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="users" element={<AdminRoute allowedRoles={['superadmin', 'moderator']}><AdminUsers /></AdminRoute>} />
                <Route path="profiles" element={<AdminRoute allowedRoles={['superadmin', 'moderator']}><AdminProfiles /></AdminRoute>} />
                <Route path="admins" element={<AdminRoute allowedRoles={['superadmin']}><AdminAdmins /></AdminRoute>} />
                <Route path="statistics" element={<AdminRoute allowedRoles={['superadmin']}><AdminStatistics /></AdminRoute>} />
                <Route path="messages" element={<AdminRoute allowedRoles={['superadmin', 'support']}><AdminMessages /></AdminRoute>} />
                <Route path="logs" element={<AdminRoute allowedRoles={['superadmin']}><AdminLogs /></AdminRoute>} />
                <Route path="docs" element={<AdminRoute allowedRoles={['superadmin', 'moderator', 'support']}><AdminDocumentation /></AdminRoute>} />
                <Route path="reports" element={<AdminRoute allowedRoles={['superadmin', 'moderator', 'support']}><AdminReports /></AdminRoute>} />
                <Route path="changelog" element={<AdminRoute allowedRoles={['superadmin']}><AdminChangelog /></AdminRoute>} />
                <Route path="notifications" element={<AdminRoute allowedRoles={['superadmin']}><AdminNotifications /></AdminRoute>} />
                <Route path="ai-intel" element={<AdminRoute allowedRoles={['superadmin']}><AdminAIHub /></AdminRoute>} />
                <Route path="security-review" element={<AdminRoute allowedRoles={['superadmin', 'moderator']}><AdminSecurityReview /></AdminRoute>} />
                <Route path="security-dossier/:uid" element={<AdminRoute allowedRoles={['superadmin', 'moderator']}><AdminSecurityDossier /></AdminRoute>} />
                <Route path="identity-search" element={<AdminRoute allowedRoles={['superadmin', 'support']}><AdminIdentitySearch /></AdminRoute>} />
                <Route path="dna-protocol" element={<AdminRoute allowedRoles={['superadmin', 'moderator']}><AdminDNAProtocol /></AdminRoute>} />
                <Route path="consultations" element={<AdminRoute allowedRoles={['superadmin', 'support']}><AdminConsultations /></AdminRoute>} />
                <Route path="settings" element={<AdminRoute allowedRoles={['superadmin']}><AdminSettings /></AdminRoute>} />
                <Route path="errors" element={<AdminRoute allowedRoles={['superadmin']}><AdminErrors /></AdminRoute>} />
                <Route path="register" element={<AdminRoute allowedRoles={['superadmin']}><AdminRegister /></AdminRoute>} />
                <Route path="sitemap" element={<AdminRoute allowedRoles={['superadmin']}><AdminSitemap /></AdminRoute>} />
              </Route>
              <Route path="/admin/login" element={<AdminLogin />} />

              {/* --- RAW ROUTES --- */}
              <Route path="/embed/:profileId" element={<EmbedWidget />} />
              <Route path="/resume/:profileId" element={<Resume />} />
              <Route path="/id/:profileId" element={<DigitalID />} />
              
              <Route path="*" element={
                <>
                  <Navbar />
                  <main className="page-container">
                    <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/about" element={<About />} />
                      <Route path="/docs" element={<Documentation />} />
                      <Route path="/changelog" element={<Changelog />} />
                      <Route path="/changelog/page/:pageNumber" element={<Changelog />} />
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/privacy" element={<PrivacyPolicy />} />
                      <Route path="/terms" element={<Terms />} />
                      <Route path="/whats-new" element={<WhatIsNew />} />
                      <Route path="/whats-new/:version" element={<WhatIsNew />} />
                      <Route path="/sitemap" element={<AdminSitemap />} />
                      <Route path="/faq" element={<FAQ />} />
                      <Route path="/colab" element={<ColabHub />} />
                      <Route path="/colab/bharatverify" element={<BharatVerifyColab />} />
                      <Route path="/colab/daivx" element={<DAIVXColab />} />
                      <Route path="/dev/:profileId" element={<PublicProfile />} />
                      <Route path="/verify/:profileId" element={<VerificationPortal />} />
                      <Route path="/login" element={<Login />} />
                      <Route path="/register" element={<Register />} />
                      <Route path="/verification-guide" element={<VerificationGuide />} />
                      <Route path="/verify-email" element={<VerifyEmail />} />
                      <Route path="/forgot-password" element={<ForgotPassword />} />
                      <Route path="/setup-2fa" element={<ProtectedRoute><TwoFactorSetup /></ProtectedRoute>} />
                      <Route path="/verify-2fa" element={<TwoFactorVerify />} />
                      <Route 
                        path="/dashboard" 
                        element={<ProtectedRoute><Dashboard /></ProtectedRoute>} 
                      />
                      <Route 
                        path="/suspended" 
                        element={<ProtectedRoute><Suspended /></ProtectedRoute>} 
                      />
                      <Route 
                        path="/report" 
                        element={<ProtectedRoute><Report /></ProtectedRoute>} 
                      />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </main>
                  <Footer />
                </>
              } />
            </Routes>
            </React.Suspense>
            <NotificationCenter />
            <DevTrackerAssistant />
          </div>
        </ErrorBoundary>
      </MaintenanceGuard>
    </Router>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
